import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { InputBar } from '~/ui';
import { useFormik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { IUsersFilters } from '~/utils/types/userFilterTypes';
import { validate } from '~/utils/validateLoyaltyFilter';
import styles from './LoyaltyFilters.module.scss';
import { useEffect, useState } from 'react';
import { FilterButtons } from '../FiltersButtons/FilterButtons';
import { getCompaniesModuleOptions } from '~/utils/getCompaniesModuleOptions';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';
import Checkbox from 'antd/es/checkbox/Checkbox';

interface IFormik {
  dates: {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };
  accural: {
    condition: { id: number; name: string; display_name: string };
    average: string;
  };
  deleted_promotion: boolean;
  announcement_organizations: { id: number; name: string; full_name: string }[];
  accommodations: { id: number; name: string; full_name: string }[] | [];
  loyalty_levels: { id: number; name: string; full_name: string }[] | [];
  promotions: { id: number; name: string; full_name: string }[] | [];
}

interface IOrderProps {
  selectedUsers: number[];
  onClick: (filters: IUsersFilters) => void;
  setIsStatusUsersModalOpen: () => void;
  isUsersLoading: boolean;
}

export const LoyaltyFilter = ({
  onClick,
  selectedUsers,
  setIsStatusUsersModalOpen,
  isUsersLoading,
}: IOrderProps) => {
  const [isAnyFieldFilled, setIsAnyFieldFilled] = useState(false);
  const toastError = useToastError();

  const { data: companies } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/user-company');
      return response.data;
    },
    queryKey: ['user-company'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: accommodations, isLoading: isAccommodationsLoading } = useQuery(
    {
      queryFn: async () => {
        const { data } = await instance.get('admin/accommodations', {
          params: {
            sort: '',
            per_page: 100,
            field: '',
            page: 1,
          },
        });
        return data;
      },
      queryKey: ['accommodations'],
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data: loyalty, isLoading: isLoyaltyLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/loyalty-level', {
        params: {
          page: 1,
          perPage: 100,
        },
      });
      return data;
    },
    queryKey: ['loyalty'],
    onError: error => {
      toastError(error);
    },
  });

  const { data: promoLoyalty, isLoading: isPromoLoyaltyLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get('admin/promo-loyalty', {
        params: {
          page: 1,
          perPage: 100,
        },
      });
      return data;
    },
    queryKey: ['promotions'],
    onError: error => {
      toastError(error);
    },
  });

  const companyModules = getCompaniesModuleOptions(companies?.companies);

  const formik = useFormik<IFormik>({
    initialValues: {
      dates: {
        start_date: null,
        end_date: null,
      },
      deleted_promotion: false,
      accural: {
        condition: { id: 0, name: '', display_name: '' },
        average: '',
      },
      announcement_organizations: [],
      accommodations: [],
      loyalty_levels: [],
      promotions: [],
    },
    validate,
    onSubmit: values => {
      const company_name = values.announcement_organizations.map(
        item => item.name
      );
      const accommodationIds = values.accommodations.map(item => item.id);
      const level_ids = values.loyalty_levels.map(item => item.id);
      const loyalty_ids = values.promotions.map(item => item.id);
      const filters = {
        ...(values.dates.start_date &&
          values.dates.end_date && {
            dates: {
              start_date: dayjs(values.dates.start_date)
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss'),
              end_date: dayjs(values.dates.end_date)
                .endOf('day')
                .format('YYYY-MM-DD HH:mm:ss'),
            },
          }),
        ...(values.accural.condition.name && values.accural.average
          ? {
              points: {
                condition: values.accural.condition.name,
                count: values.accural.average,
              },
            }
          : {}),
        ...(values.announcement_organizations.length !== 0
          ? {
              companies: {
                names: company_name,
              },
            }
          : {}),
        ...(!!values.loyalty_levels.length && {
          loyalty_level: {
            level_ids,
          },
        }),
        ...(!!values.promotions.length && {
          promo_loyalty: {
            loyalty_ids,
            by_deleted: values.deleted_promotion ? 1 : 0,
          },
        }),
        ...(!!values.accommodations.length && {
          accommodation_ids: accommodationIds,
        }),
      };
      onClick(filters);
    },
  });

  const handleResetForm = () => {
    formik.resetForm();
    onClick({});
  };

  useEffect(() => {
    const isFilled =
      !!formik.values.dates.start_date ||
      !!formik.values.dates.end_date ||
      !!formik.values.accommodations.length ||
      !!formik.values.announcement_organizations.length ||
      (!!formik.values.accural.condition.name &&
        !!formik.values.accural.average) ||
      !!formik.values.loyalty_levels.length ||
      !!formik.values.promotions.length;

    setIsAnyFieldFilled(isFilled);
  }, [formik.values]);

  return (
    <div className={styles.orderContainer}>
      <form onSubmit={formik.handleSubmit} className={styles.formWrapper}>
        <div className={styles.companyWrapper}>
          <CustomSelect
            label={t('company')}
            options={companyModules}
            value={formik.values.announcement_organizations}
            onChange={value => {
              formik.setFieldValue('announcement_organizations', value);
            }}
            placeholder={t('validate_organizations')}
            disabled={isUsersLoading}
            multiselect
            className={styles.companySelect}
          />
          <div className={styles.dateWrapper}>
            <CustomDatePicker
              title={t('accrual_write_off_period')}
              value={[
                formik.values.dates.start_date,
                formik.values.dates.end_date,
              ]}
              placeholder={[t('date'), t('date')]}
              onChange={(name, value) => {
                if (name === 'start_date') {
                  formik.setFieldValue('dates.start_date', value);
                } else {
                  formik.setFieldValue('dates.end_date', value);
                }
              }}
            />
          </div>
        </div>
        <div className={styles.operators}>
          <div className={styles.operatorWrapper}>
            <CustomSelect
              label={t('accommodations')}
              placeholder={t('select_accommodation_facility')}
              options={accommodations?.data}
              value={formik.values.accommodations}
              onChange={value => formik.setFieldValue('accommodations', value)}
              disabled={isAccommodationsLoading}
              multiselect
            />
          </div>
          <div className={styles.operatorWrapper}>
            <span className={styles.operatorsLabel}>{t('accrual_points')}</span>
            <div className={styles.operatorLine}>
              <CustomSelect
                value={formik.values.accural.condition}
                options={[
                  { id: 1, display_name: `${t('more')}`, name: '>' },
                  { id: 2, display_name: `${t('less')}`, name: '<' },
                  { id: 3, display_name: `${t('equal')}`, name: '=' },
                  { id: 4, display_name: `${t('more_or_equal')}`, name: '>=' },
                  { id: 5, display_name: `${t('less_or_equal')}`, name: '<=' },
                ]}
                placeholder={t('select_operator')}
                onChange={value =>
                  formik.setFieldValue('accural.condition', value)
                }
                error={
                  formik.touched.accural?.condition?.name
                    ? (formik.errors.accural?.condition?.name as string)
                    : undefined
                }
              />
              <InputBar
                value={formik.values.accural.average}
                name="accural.average"
                onChange={formik.handleChange}
                classNameInputWrapper={styles.input}
                placeholder={t('enter_the_number')}
                label=""
                errors={formik.errors.accural?.average}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.accural?.average &&
                  Boolean(formik.errors.accural?.average)
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.loyaltyWrapper}>
          <div className={styles.selectContainer}>
            <span className={styles.loyaltyLabel}>{t('promotion')}</span>
            <CustomSelect
              value={formik.values.promotions}
              options={promoLoyalty?.data}
              placeholder={t('placeholder_promotion')}
              onChange={value => formik.setFieldValue('promotions', value)}
              disabled={isPromoLoyaltyLoading}
              multiselect
            />
          </div>
          <div className={styles.selectContainer}>
            <span className={styles.loyaltyLabel}>{t('loyalty_level')}</span>
            <CustomSelect
              value={formik.values.loyalty_levels}
              options={loyalty?.data}
              placeholder={t('placeholder_loyalty')}
              onChange={value => formik.setFieldValue('loyalty_levels', value)}
              disabled={isLoyaltyLoading}
              multiselect
            />
          </div>
        </div>
        {!!formik.values?.promotions.length && (
          <div className={styles.checkboxWrapper}>
            <Checkbox
              value={formik.values?.deleted_promotion}
              checked={formik.values?.deleted_promotion}
              onChange={e =>
                formik.setFieldValue('deleted_promotion', e.target.checked)
              }
            />
            <span className={styles.checkboxLabel}>
              {t('deleted_promotions')}
            </span>
          </div>
        )}
        <FilterButtons
          setIsModalOpen={setIsStatusUsersModalOpen}
          isAnyFieldFilled={isAnyFieldFilled}
          selectedUsers={selectedUsers}
          resetForm={handleResetForm}
          isLoading={isUsersLoading}
        />
      </form>
    </div>
  );
};
